import type { ViewQueryNode } from '@salescore/core'
import { Array, Filter, HashSet } from 'effect'

import type { NewRecordChange } from '../../rsheet/action/addNewRecord/addNewRecords'
import type { DeepReadonly } from '../../rsheet/types/DeepReadonly'
import type { RecordChange } from '../../rsheet/util/updateViewRecords'
import { keyFunction } from '../generateChangesFromCopy/generateParentToChildRelationChanges'

// 新規レコード作成の権限で newRecordChanges / recordChanges を選別する。
// 権限判定は addEmptyRecordMutation / ContextMenu / SheetNavigation と同じく、
// ビューの権限設定とユーザーロールを評価した sheet-create-new-record の結果 (canCreateNewRecord) と
// カスタムオブジェクトの個別権限 (canCreateSalescoreObjectRecord) のどちらかがあれば許可する。
// 個別権限は前者より優先する仕様のため、前者だけで判定すると個別権限で許可したユーザーの新規作成が落ちる (#9586)。
export const selectAllowedNewRecordChanges = ({
  ability,
  newRecordChanges,
  recordChanges,
  canBeCreatedWithoutAbility,
}: {
  ability: { canCreateNewRecord: boolean; canCreateSalescoreObjectRecord: boolean }
  newRecordChanges: DeepReadonly<NewRecordChange[]>
  recordChanges: DeepReadonly<RecordChange[]>
  canBeCreatedWithoutAbility: (node: DeepReadonly<ViewQueryNode>) => boolean
}): {
  allowedNewRecordChanges: DeepReadonly<NewRecordChange[]>
  allowedRecordChanges: DeepReadonly<RecordChange[]>
  hasDeniedNewRecordChanges: boolean
} => {
  if (ability.canCreateNewRecord || ability.canCreateSalescoreObjectRecord) {
    return {
      allowedNewRecordChanges: newRecordChanges,
      allowedRecordChanges: recordChanges,
      hasDeniedNewRecordChanges: false,
    }
  }

  const [allowedNewRecordChanges, deniedNewRecordChanges] = Array.partition(
    newRecordChanges,
    Filter.fromPredicate((newRecordChange) => canBeCreatedWithoutAbility(newRecordChange.node)),
  )
  const keysToDiscard = HashSet.fromIterable(
    deniedNewRecordChanges.map((newRecordChange) =>
      keyFunction({
        nodePath: newRecordChange.node.path,
        rowIndex: newRecordChange.rowIndex,
        innerRowIndex: newRecordChange.innerRowIndex,
      }),
    ),
  )

  return {
    allowedNewRecordChanges,
    // 作成を拒否した新規レコードは存在しないので、そのレコードに対する変更も破棄する
    allowedRecordChanges: recordChanges.filter(
      (recordChange) => !HashSet.has(keysToDiscard, keyFunction(recordChange)),
    ),
    hasDeniedNewRecordChanges: Array.isArrayNonEmpty(deniedNewRecordChanges),
  }
}
